<template>
  <div class="user-edit-page py-3">
    <header class="user-toolbar">
      <div class="user-toolbar__title">
        <span class="user-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Profile Settings</h1>
          <p>Update your photo, contact details, and password</p>
        </div>
      </div>
      <div class="user-toolbar__actions">
        <CButton size="sm" class="back-btn" @click="goBack">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="back-btn__icon" aria-hidden="true">
            <line x1="19" y1="12" x2="5" y2="12"/>
            <polyline points="12 19 5 12 12 5"/>
          </svg>
          <span>Back</span>
        </CButton>
      </div>
    </header>

    <div v-if="outstandingRequired.length" class="user-required-note">
      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
      <span v-if="gate.mandatory">
        <b>Required before you can continue:</b> {{ outstandingLabel }}.
      </span>
      <span v-else>
        <b>Still required:</b> {{ outstandingLabel }}.
        {{ gate.warningsRemaining }} more
        {{ gate.warningsRemaining === 1 ? 'reminder' : 'reminders' }}
        before these become compulsory.
      </span>
    </div>

    <CAlert v-if="errorMessage" color="danger" show class="user-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="user-alert">{{ successMessage }}</CAlert>

    <div v-if="isLoading" class="user-loading-card">
      <CSpinner size="sm" class="mr-2" />
      <span>Loading profile…</span>
    </div>

    <form v-else class="user-form" @submit.prevent="save">
      <!-- Profile section -->
      <section class="user-section">
        <header class="user-section__head">
          <div class="user-section__heading">
            <span class="user-section__step">1</span>
            <div>
              <h2>Profile</h2>
              <p>Your photo and contact details</p>
            </div>
          </div>
        </header>

        <div class="user-section__body">
          <div class="profile-row">
            <div class="profile-photo">
              <div
                class="profile-photo__avatar"
                :style="!showPhotoImage ? { background: avatarGradient } : null"
              >
                <img
                  v-if="showPhotoImage"
                  :src="photoPreview"
                  alt="Profile preview"
                  @error="onPhotoLoadError"
                />
                <span v-else>{{ avatarInitials }}</span>
              </div>
              <input
                ref="photoInput"
                type="file"
                accept="image/*"
                class="profile-photo__input"
                @change="onPhotoSelected"
              />
              <div class="profile-photo__actions">
                <button type="button" class="ghost-btn" @click="triggerPhotoPicker">
                  {{ photoPreview ? 'Change Photo' : 'Upload Photo' }}
                </button>
                <button v-if="photoPreview" type="button" class="ghost-btn ghost-btn--danger" @click="clearPhoto">
                  Remove
                </button>
              </div>
              <p class="profile-photo__hint">PNG or JPG, up to 2 MB.</p>
            </div>

            <div class="profile-fields">
              <div class="profile-summary">
                <div class="profile-summary__name">{{ form.name || 'Unnamed user' }}</div>
                <div class="profile-summary__tags">
                  <span :class="['role-pill', roleClass(readonly.role)]">{{ formatRole(readonly.role) }}</span>
                  <span class="role-pill role-pill--gray">{{ readonly.organisation || '—' }}</span>
                  <span v-if="readonly.department && readonly.department !== '—'" class="role-pill role-pill--gray">{{ readonly.department }}</span>
                  <span v-if="readonly.designation" class="role-pill role-pill--gray">{{ readonly.designation }}</span>
                  <span v-if="readonly.employee_code" class="role-pill role-pill--gray">#{{ readonly.employee_code }}</span>
                </div>
                <div v-if="hasReadonlyHrDetails" class="profile-summary__hr">
                  <span v-if="readonly.date_of_joining"><b>Joined:</b> {{ readonly.date_of_joining }}</span>
                  <span v-if="readonly.employment_type"><b>Type:</b> {{ formatRole(readonly.employment_type) }}</span>
                  <span v-if="readonly.employment_status"><b>Status:</b> {{ formatRole(readonly.employment_status) }}</span>
                </div>
              </div>

              <div class="form-grid">
                <div class="form-field">
                  <label class="form-label" for="profile-name">
                    Full name
                    <span class="form-label__lock" title="Your name can only be changed by an administrator">
                      <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                      Locked
                    </span>
                  </label>
                  <input
                    id="profile-name"
                    :value="form.name"
                    type="text"
                    class="form-input form-input--locked"
                    autocomplete="name"
                    readonly
                    disabled
                    aria-readonly="true"
                  />
                  <span class="form-hint">Contact an administrator to update your name.</span>
                </div>

                <div class="form-field">
                  <label class="form-label" for="profile-email">Email</label>
                  <input
                    id="profile-email"
                    v-model.trim="form.email"
                    type="email"
                    class="form-input"
                    autocomplete="email"
                    placeholder="name@example.com"
                  />
                </div>

                <div class="form-field">
                  <label class="form-label" for="profile-phone">Phone</label>
                  <input id="profile-phone" v-model.trim="form.phone" type="tel" class="form-input" autocomplete="tel"
                    placeholder="+91 98xxxxxxxx" />
                </div>
                <div class="form-field">
                  <label class="form-label" for="profile-alt-phone">Alternate phone</label>
                  <input id="profile-alt-phone" v-model.trim="form.alternate_phone" type="tel" class="form-input" />
                </div>
                <div class="form-field">
                  <label class="form-label">Date of birth</label>
                  <SingleDatePicker v-model="form.date_of_birth" />
                </div>
                <div class="form-field">
                  <label class="form-label">Gender</label>
                  <AppDropdown v-model="form.gender" :options="genderOptions" placeholder="Select gender" />
                </div>
                <div class="form-field">
                  <label class="form-label">Blood group</label>
                  <AppDropdown v-model="form.blood_group" :options="bloodGroupOptions" placeholder="Select" />
                </div>
                <div class="form-field">
                  <label class="form-label">Marital status</label>
                  <AppDropdown v-model="form.marital_status" :options="maritalOptions" placeholder="Select" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Address section -->
      <section class="user-section">
        <header class="user-section__head">
          <div class="user-section__heading">
            <span class="user-section__step">2</span>
            <div>
              <h2>Address</h2>
              <p>Where you are based</p>
            </div>
          </div>
        </header>
        <div class="user-section__body">
          <div class="form-grid form-grid--single">
            <div class="form-field">
              <label class="form-label" for="profile-address">Street address</label>
              <textarea id="profile-address" v-model="form.address" rows="3" class="form-input form-textarea"
                placeholder="House / building, street, locality"></textarea>
            </div>
            <div class="form-grid form-grid--quad">
              <div class="form-field">
                <label class="form-label" for="profile-city">City</label>
                <input id="profile-city" v-model.trim="form.city" type="text" class="form-input" />
              </div>
              <div class="form-field">
                <label class="form-label" for="profile-state">State</label>
                <input id="profile-state" v-model.trim="form.state" type="text" class="form-input" />
              </div>
              <div class="form-field">
                <label class="form-label" for="profile-pincode">Pincode</label>
                <input id="profile-pincode" v-model.trim="form.pincode" type="text" inputmode="numeric"
                  maxlength="10" class="form-input" />
              </div>
              <div class="form-field">
                <label class="form-label" for="profile-country">Country</label>
                <input id="profile-country" v-model.trim="form.country" type="text" class="form-input" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Emergency contact -->
      <section class="user-section">
        <header class="user-section__head">
          <div class="user-section__heading">
            <span class="user-section__step">3</span>
            <div>
              <h2>Emergency Contact</h2>
              <p>Whom we can reach if there's an emergency</p>
            </div>
          </div>
        </header>
        <div class="user-section__body">
          <div class="form-grid form-grid--triple">
            <div class="form-field">
              <label class="form-label" for="profile-ec-name">Contact name</label>
              <input id="profile-ec-name" v-model.trim="form.emergency_contact_name" type="text" class="form-input" />
            </div>
            <div class="form-field">
              <label class="form-label" for="profile-ec-phone">Contact phone</label>
              <input id="profile-ec-phone" v-model.trim="form.emergency_contact_phone" type="tel" class="form-input" />
            </div>
            <div class="form-field">
              <label class="form-label">Relation</label>
              <AppDropdown v-model="form.emergency_contact_relation" :options="relationOptions"
                placeholder="Select relation" searchable />
            </div>
          </div>
        </div>
      </section>

      <!-- KYC & Bank -->
      <section class="user-section">
        <header class="user-section__head">
          <div class="user-section__heading">
            <span class="user-section__step">4</span>
            <div>
              <h2>KYC &amp; Bank</h2>
              <p>Identity proof and payroll account details</p>
            </div>
          </div>
          <span class="user-hint-pill">Sensitive</span>
        </header>
        <div class="user-section__body">
          <div class="form-grid">
            <div class="form-field">
              <label class="form-label" for="profile-pan">PAN number</label>
              <input id="profile-pan" v-model.trim="form.pan_number" type="text" class="form-input"
                maxlength="10" style="text-transform: uppercase" placeholder="ABCDE1234F" />
            </div>
            <div class="form-field">
              <label class="form-label" for="profile-aadhaar">Aadhaar number</label>
              <input id="profile-aadhaar" v-model.trim="form.aadhaar_number" type="text" inputmode="numeric"
                maxlength="14" class="form-input" placeholder="12-digit" />
            </div>
            <div class="form-field">
              <label class="form-label" for="profile-bank-acc">Bank account number</label>
              <input id="profile-bank-acc" v-model.trim="form.bank_account_number" type="text" class="form-input" />
            </div>
            <div class="form-field">
              <label class="form-label" for="profile-bank-name">Bank name</label>
              <input id="profile-bank-name" v-model.trim="form.bank_name" type="text" class="form-input" />
            </div>
            <div class="form-field">
              <label class="form-label" for="profile-bank-ifsc">IFSC</label>
              <input id="profile-bank-ifsc" v-model.trim="form.bank_ifsc" type="text" class="form-input"
                maxlength="11" style="text-transform: uppercase" placeholder="SBIN0001234" />
            </div>
          </div>
        </div>
      </section>

      <!-- Change password section -->
      <section class="user-section">
        <header class="user-section__head">
          <div class="user-section__heading">
            <span class="user-section__step">5</span>
            <div>
              <h2>Change Password</h2>
              <p>Leave blank to keep your current password</p>
            </div>
          </div>
          <span class="user-hint-pill">Optional</span>
        </header>

        <div class="user-section__body">
          <div class="form-grid form-grid--triple">
            <div class="form-field">
              <label class="form-label" for="profile-current-password">Current password</label>
              <div class="password-wrap">
                <input
                  id="profile-current-password"
                  v-model="form.currentPassword"
                  :type="showCurrentPassword ? 'text' : 'password'"
                  class="form-input"
                  autocomplete="current-password"
                  placeholder="Required to change password"
                />
                <button
                  type="button"
                  class="password-toggle"
                  :aria-label="showCurrentPassword ? 'Hide password' : 'Show password'"
                  @click="showCurrentPassword = !showCurrentPassword"
                >
                  {{ showCurrentPassword ? 'Hide' : 'Show' }}
                </button>
              </div>
            </div>

            <div class="form-field">
              <label class="form-label" for="profile-new-password">New password</label>
              <div class="password-wrap">
                <input
                  id="profile-new-password"
                  v-model="form.password"
                  :type="showNewPassword ? 'text' : 'password'"
                  class="form-input"
                  autocomplete="new-password"
                  placeholder="At least 6 characters"
                />
                <button
                  type="button"
                  class="password-toggle"
                  :aria-label="showNewPassword ? 'Hide password' : 'Show password'"
                  @click="showNewPassword = !showNewPassword"
                >
                  {{ showNewPassword ? 'Hide' : 'Show' }}
                </button>
              </div>
              <div v-if="form.password" class="password-strength" :class="`password-strength--${passwordStrength.key}`">
                <span class="password-strength__bar"><span :style="{ width: passwordStrength.percent + '%' }"></span></span>
                <span class="password-strength__label">{{ passwordStrength.label }}</span>
              </div>
            </div>

            <div class="form-field">
              <label class="form-label" for="profile-confirm-password">Confirm new password</label>
              <input
                id="profile-confirm-password"
                v-model="form.passwordConfirmation"
                :type="showNewPassword ? 'text' : 'password'"
                class="form-input"
                :class="{ 'form-input--error': passwordMismatch }"
                autocomplete="new-password"
                placeholder="Re-enter password"
              />
              <span v-if="passwordMismatch" class="form-error">Passwords do not match</span>
            </div>
          </div>
        </div>
      </section>

      <footer class="user-form__footer">
        <span class="form-meta">{{ lastFetchedLabel }}</span>
        <div class="user-form__actions">
          <CButton
            type="button"
            size="sm"
            class="back-btn"
            :disabled="isSaving"
            @click="loadProfile"
          >
            Discard changes
          </CButton>
          <CButton
            type="submit"
            size="sm"
            class="submit-btn"
            :disabled="isSaving || !canSubmit"
          >
            <svg v-if="!isSaving" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="submit-btn__icon" aria-hidden="true">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <span>{{ isSaving ? 'Saving…' : 'Save Changes' }}</span>
          </CButton>
        </div>
      </footer>
    </form>
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import SingleDatePicker from '@/components/SingleDatePicker.vue'
import completion, { applyCompletion } from '@/utils/profileCompletion'

const PHOTO_MAX_BYTES = 2 * 1024 * 1024

const ROLE_PILL_CLASS = {
  superadmin: 'role-pill--violet',
  admin: 'role-pill--violet',
  manager: 'role-pill--blue',
  telesales: 'role-pill--amber',
  'tele-sales': 'role-pill--amber',
  field: 'role-pill--teal',
  staff: 'role-pill--green',
}

const GENDER_OPTIONS = ['Male', 'Female', 'Other', 'Prefer not to say']
const MARITAL_OPTIONS = ['Single', 'Married', 'Divorced', 'Widowed', 'Separated']
const BLOOD_GROUP_OPTIONS = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-']
const RELATION_OPTIONS = ['Father', 'Mother', 'Spouse', 'Sibling', 'Child', 'Guardian', 'Friend', 'Other']

const SELF_FIELD_KEYS = [
  'phone',
  'alternate_phone',
  'address',
  'city',
  'state',
  'pincode',
  'country',
  'date_of_birth',
  'gender',
  'blood_group',
  'marital_status',
  'emergency_contact_name',
  'emergency_contact_phone',
  'emergency_contact_relation',
  'pan_number',
  'aadhaar_number',
  'bank_account_number',
  'bank_name',
  'bank_ifsc',
]

function emptySelfFields () {
  return SELF_FIELD_KEYS.reduce((acc, key) => {
    acc[key] = ''
    return acc
  }, {})
}

function pickDateOnly (value) {
  if (!value) return ''
  const str = String(value)
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str
  const match = str.match(/^(\d{4}-\d{2}-\d{2})/)
  return match ? match[1] : ''
}

export default {
  name: 'ProfileSettings',
  components: { AppDropdown, SingleDatePicker },
  data () {
    return {
      gate: completion.state,
      isLoading: false,
      isSaving: false,
      errorMessage: '',
      successMessage: '',
      showCurrentPassword: false,
      showNewPassword: false,
      lastFetchedAt: null,
      form: {
        name: '',
        email: '',
        currentPassword: '',
        password: '',
        passwordConfirmation: '',
        ...emptySelfFields(),
      },
      readonly: {
        role: '',
        organisation: '',
        department: '',
        employee_code: '',
        designation: '',
        date_of_joining: '',
        employment_type: '',
        employment_status: '',
      },
      photoExisting: '',
      photoFile: null,
      photoPreview: '',
      photoRemoved: false,
      photoLoadFailed: false,
      genderOptions: GENDER_OPTIONS.map((v) => ({ value: v, label: v })),
      maritalOptions: MARITAL_OPTIONS.map((v) => ({ value: v, label: v })),
      bloodGroupOptions: BLOOD_GROUP_OPTIONS.map((v) => ({ value: v, label: v })),
      relationOptions: RELATION_OPTIONS.map((v) => ({ value: v, label: v })),
    }
  },
  computed: {
    apiUrl () {
      return `${this.$apiAdress}/api/profile`
    },
    showPhotoImage () {
      return !!this.photoPreview && !this.photoLoadFailed
    },
    passwordMismatch () {
      return !!this.form.password && this.form.password !== this.form.passwordConfirmation
    },
    canSubmit () {
      if (this.form.password && this.passwordMismatch) return false
      if (this.form.password && !this.form.currentPassword) return false
      return true
    },
    avatarInitials () {
      const source = this.form.name || this.form.email || 'U'
      const initials = source
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0].toUpperCase())
        .join('')
      return initials || 'U'
    },
    avatarGradient () {
      const palette = [
        'linear-gradient(135deg, #0e5b46 0%, #16a07a 100%)',
        'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        'linear-gradient(135deg, #6d28d9 0%, #a855f7 100%)',
        'linear-gradient(135deg, #b45309 0%, #f59e0b 100%)',
        'linear-gradient(135deg, #be185d 0%, #ec4899 100%)',
        'linear-gradient(135deg, #115e59 0%, #14b8a6 100%)',
      ]
      const seed = String(this.form.name || this.form.email || '?')
      let hash = 0
      for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
      return palette[hash % palette.length]
    },
    passwordStrength () {
      const value = this.form.password || ''
      if (!value) return { key: 'weak', label: '', percent: 0 }
      let score = 0
      if (value.length >= 8) score += 1
      if (value.length >= 12) score += 1
      if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score += 1
      if (/\d/.test(value)) score += 1
      if (/[^A-Za-z0-9]/.test(value)) score += 1
      const buckets = [
        { key: 'weak', label: 'Weak', percent: 25 },
        { key: 'weak', label: 'Weak', percent: 35 },
        { key: 'fair', label: 'Fair', percent: 55 },
        { key: 'good', label: 'Good', percent: 75 },
        { key: 'strong', label: 'Strong', percent: 100 },
      ]
      return buckets[Math.min(score, buckets.length - 1)]
    },
    lastFetchedLabel () {
      if (!this.lastFetchedAt) return 'Not fetched yet'
      return `Last fetched: ${this.lastFetchedAt.toLocaleString()}`
    },
    // Mirrors the completion gate so this page names the same outstanding
    // details the modal asks for, instead of silently failing on save.
    outstandingRequired () {
      return this.gate.complete ? [] : (this.gate.missing || [])
    },
    outstandingLabel () {
      return this.outstandingRequired.map((field) => field.label).join(', ')
    },
    hasReadonlyHrDetails () {
      return !!(this.readonly.date_of_joining || this.readonly.employment_type || this.readonly.employment_status)
    },
  },
  created () {
    this.loadProfile()
  },
  methods: {
    goBack () {
      if (window.history.length > 1) this.$router.go(-1)
      else this.$router.push('/dashboard')
    },
    loadProfile () {
      this.isLoading = true
      this.errorMessage = ''
      this.successMessage = ''

      axios.get(this.apiUrl)
        .then((response) => {
          this.applyProfile(response.data || {})
          this.lastFetchedAt = new Date()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message || 'Unable to load your profile right now.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    applyProfile (data) {
      this.form.name = data.name || ''
      this.form.email = data.email || ''
      this.form.currentPassword = ''
      this.form.password = ''
      this.form.passwordConfirmation = ''
      this.readonly.role = data.role || 'None'
      this.readonly.organisation = (data.organisation && data.organisation.name) || ''
      this.readonly.department = (data.department && data.department.name) || ''
      this.readonly.employee_code = data.employee_code || ''
      this.readonly.designation = data.designation || ''
      this.readonly.date_of_joining = pickDateOnly(data.date_of_joining)
      this.readonly.employment_type = data.employment_type || ''
      this.readonly.employment_status = data.employment_status || ''

      SELF_FIELD_KEYS.forEach((key) => {
        if (key === 'date_of_birth') {
          this.form[key] = pickDateOnly(data[key])
        } else {
          this.form[key] = data[key] != null ? data[key] : ''
        }
      })

      this.photoExisting = typeof data.image === 'string' ? data.image : ''
      this.photoPreview = this.photoExisting
      this.photoFile = null
      this.photoRemoved = false
      this.photoLoadFailed = false

      applyCompletion(data.profile_completion)
      this.syncLocalAuthUser(data)
    },
    formatRole (role) {
      if (!role || role === 'None') return 'No role'
      return role.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    },
    roleClass (role) {
      const key = (role || '').toLowerCase().trim()
      return ROLE_PILL_CLASS[key] || 'role-pill--gray'
    },
    triggerPhotoPicker () {
      const input = this.$refs.photoInput
      if (input) input.click()
    },
    onPhotoSelected (event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      if (file.size > PHOTO_MAX_BYTES) {
        this.errorMessage = 'Image must be 2 MB or smaller.'
        event.target.value = ''
        return
      }
      this.errorMessage = ''
      this.photoFile = file
      this.photoRemoved = false
      this.photoLoadFailed = false
      const reader = new FileReader()
      reader.onload = (e) => { this.photoPreview = e.target.result }
      reader.readAsDataURL(file)
      event.target.value = ''
    },
    clearPhoto () {
      this.photoFile = null
      this.photoPreview = ''
      this.photoRemoved = !!this.photoExisting
    },
    onPhotoLoadError () {
      this.photoLoadFailed = true
    },
    save () {
      this.errorMessage = ''
      this.successMessage = ''

      if (this.form.password && this.passwordMismatch) {
        this.errorMessage = 'New password and confirmation do not match.'
        return
      }

      const payload = new FormData()
      payload.append('name', this.form.name || '')
      payload.append('email', this.form.email || '')

      if (this.form.password) {
        payload.append('current_password', this.form.currentPassword)
        payload.append('password', this.form.password)
        payload.append('password_confirmation', this.form.passwordConfirmation)
      }

      SELF_FIELD_KEYS.forEach((key) => {
        let value = this.form[key]
        if (typeof value === 'string') value = value.trim()
        if (key === 'pan_number' && value) value = value.toUpperCase()
        if (key === 'bank_ifsc' && value) value = value.toUpperCase()
        // FormData has no native "null" — send empty string to clear.
        payload.append(key, value === null || value === undefined ? '' : value)
      })

      if (this.photoFile) {
        payload.append('profile_photo', this.photoFile)
      } else if (this.photoRemoved) {
        payload.append('remove_profile_photo', '1')
      }

      this.isSaving = true

      axios.post(this.apiUrl, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
        .then((response) => {
          this.successMessage = 'Profile updated successfully.'
          this.applyProfile(response.data || {})
          this.lastFetchedAt = new Date()
        })
        .catch((error) => {
          console.log(error)
          const status = error?.response?.status
          if (status === 422) {
            const data = error.response.data || {}
            if (data.errors) {
              const first = Object.values(data.errors)[0]
              this.errorMessage = Array.isArray(first) ? first[0] : String(first)
            } else {
              this.errorMessage = data.message || 'Some of your changes are not valid.'
            }
          } else {
            this.errorMessage = error?.response?.data?.message || 'Unable to save your profile right now.'
          }
        })
        .finally(() => {
          this.isSaving = false
        })
    },
    syncLocalAuthUser (data) {
      try {
        const raw = localStorage.getItem('auth_user')
        const current = raw ? JSON.parse(raw) : {}
        const merged = {
          ...current,
          id: data.id ?? current.id,
          name: data.name ?? current.name,
          email: data.email ?? current.email,
          image: Object.prototype.hasOwnProperty.call(data, 'image') ? data.image : current.image,
          organisation_id: Object.prototype.hasOwnProperty.call(data, 'organisation_id')
            ? data.organisation_id
            : current.organisation_id,
        }
        localStorage.setItem('auth_user', JSON.stringify(merged))
      } catch (error) {
        console.log(error)
      }
    },
  },
}
</script>

<style scoped>
.user-required-note {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.85rem;
  padding: 0.6rem 0.8rem;
  border: 1px solid #fde68a;
  border-radius: 10px;
  background: #fffbeb;
  color: #92400e;
  font-size: 0.78rem;
  line-height: 1.45;
}

.user-required-note svg {
  flex: 0 0 auto;
  margin-top: 0.1rem;
}

.user-edit-page {
  padding: 0.25rem 0 1.25rem;
}

/* ── Toolbar ── */
.user-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.user-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.user-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.user-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.user-toolbar__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.user-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.user-toolbar__actions >>> .btn.back-btn,
.user-form__actions >>> .btn.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.user-toolbar__actions >>> .btn.back-btn:hover:not(:disabled),
.user-form__actions >>> .btn.back-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.back-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
  transition: transform 0.2s ease;
}

.user-toolbar__actions >>> .btn.back-btn:hover:not(:disabled) .back-btn__icon {
  transform: translateX(-2px);
}

/* ── Submit button ── */
.user-form__actions >>> .btn.submit-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 36px;
  padding: 0 1.1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.user-form__actions >>> .btn.submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
  color: #ffffff;
}

.user-form__actions >>> .btn.submit-btn:active:not(:disabled) {
  transform: translateY(0);
  filter: brightness(0.96);
}

.user-form__actions >>> .btn.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  filter: saturate(0.6);
}

.submit-btn__icon {
  flex-shrink: 0;
  display: block;
}

/* ── Alert ── */
.user-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

/* ── Section card ── */
.user-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
  overflow: hidden;
}

.user-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
}

.user-section__heading {
  display: flex;
  align-items: center;
  gap: 0.7rem;
}

.user-section__step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #ffffff;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22);
  flex-shrink: 0;
}

.user-section__heading h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.user-section__heading p {
  margin: 0;
  font-size: 0.74rem;
  color: #6b7280;
}

.user-hint-pill {
  font-size: 0.7rem;
  font-weight: 600;
  color: #6b7280;
  background: #f1f5f9;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  letter-spacing: 0.02em;
}

.user-section__body {
  padding: 1.1rem 1rem;
}

/* ── Loading ── */
.user-loading-card {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 3rem 1rem;
  color: #6b7280;
  font-size: 0.85rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

/* ── Profile row ── */
.profile-row {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 1.5rem;
  align-items: flex-start;
}

.profile-photo {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.6rem;
  padding: 0.75rem;
  border: 1px dashed #d8dde6;
  border-radius: 12px;
  background: #fbfcfe;
}

.profile-photo__avatar {
  width: 110px;
  height: 110px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  overflow: hidden;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.18);
}

.profile-photo__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-photo__input {
  display: none;
}

.profile-photo__actions {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
  justify-content: center;
}

.profile-photo__hint {
  margin: 0;
  font-size: 0.7rem;
  color: #6b7280;
}

.ghost-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  height: 30px;
  padding: 0 0.75rem;
  border: 1px solid #d4ece1;
  border-radius: 7px;
  background: #ffffff;
  color: #0e5b46;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.ghost-btn:hover {
  background: #f5fbf8;
  border-color: #16a07a;
}

.ghost-btn--danger {
  color: #b91c1c;
  border-color: #fecaca;
}

.ghost-btn--danger:hover {
  background: #fef2f2;
  border-color: #ef4444;
}

.profile-fields {
  min-width: 0;
}

.profile-summary {
  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;
  border-radius: 10px;
  background: #fbfcfe;
  border: 1px solid #eef1f7;
}

.profile-summary__name {
  font-size: 1rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.profile-summary__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 0.45rem;
}

/* ── Role pill ── */
.role-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.6rem;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: capitalize;
  white-space: nowrap;
}

.role-pill--gray   { background: #f1f5f9; color: #475569; }
.role-pill--violet { background: #ede9fe; color: #5b21b6; }
.role-pill--blue   { background: #dbeafe; color: #1e40af; }
.role-pill--amber  { background: #fef3c7; color: #92400e; }
.role-pill--teal   { background: #ccfbf1; color: #115e59; }
.role-pill--green  { background: #dff5ea; color: #0e5b46; }

/* ── Form grids ── */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem 1rem;
}

.form-grid--triple {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.form-grid--single {
  grid-template-columns: 1fr;
}

.form-grid--quad {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.form-textarea {
  height: auto;
  min-height: 80px;
  padding: 0.55rem 0.7rem;
  resize: vertical;
  line-height: 1.5;
}

.profile-summary__hr {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.5rem;
  font-size: 0.74rem;
  color: #4f5560;
}

.profile-summary__hr b {
  font-weight: 600;
  color: #1f2a44;
}

.form-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.form-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.form-label__lock {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  margin-left: 0.4rem;
  padding: 0.08rem 0.4rem;
  border-radius: 999px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  vertical-align: middle;
}

.form-input--locked {
  background: #f4f6fa;
  color: #6b7280;
  cursor: not-allowed;
  border-color: #e2e7f0;
}

.form-hint {
  margin-top: 0.3rem;
  font-size: 0.72rem;
  color: #6b7280;
}

.form-input {
  width: 100%;
  height: 38px;
  padding: 0.3rem 0.7rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.85rem;
  color: #2f3640;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  appearance: none;
  -webkit-appearance: none;
}

.form-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.form-input--error,
.form-input--error:focus {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.18);
}

.form-error {
  margin-top: 0.3rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: #b91c1c;
}

/* ── Password ── */
.password-wrap {
  position: relative;
}

.password-wrap .form-input {
  padding-right: 4rem;
}

.password-toggle {
  position: absolute;
  top: 50%;
  right: 0.4rem;
  transform: translateY(-50%);
  height: 28px;
  padding: 0 0.6rem;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #0e5b46;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.password-toggle:hover {
  background: #f5fbf8;
  border-color: #d4ece1;
}

.password-strength {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.45rem;
}

.password-strength__bar {
  flex: 1;
  height: 5px;
  background: #eef1f7;
  border-radius: 999px;
  overflow: hidden;
}

.password-strength__bar > span {
  display: block;
  height: 100%;
  border-radius: 999px;
  transition: width 0.2s ease, background 0.2s ease;
}

.password-strength__label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.password-strength--weak   .password-strength__bar > span { background: #ef4444; }
.password-strength--weak   .password-strength__label { color: #b91c1c; }
.password-strength--fair   .password-strength__bar > span { background: #f59e0b; }
.password-strength--fair   .password-strength__label { color: #b45309; }
.password-strength--good   .password-strength__bar > span { background: #0ea5e9; }
.password-strength--good   .password-strength__label { color: #0369a1; }
.password-strength--strong .password-strength__bar > span { background: #16a07a; }
.password-strength--strong .password-strength__label { color: #0e5b46; }

/* ── Footer ── */
.user-form__footer {
  position: sticky;
  bottom: 0.5rem;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 0.85rem 1rem;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 -4px 12px rgba(15, 23, 42, 0.06), 0 2px 6px rgba(15, 23, 42, 0.04);
}

.form-meta {
  font-size: 0.78rem;
  color: #6b7280;
}

.user-form__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* ── Responsive ── */
@media (max-width: 991px) {
  .form-grid--quad {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .profile-row {
    grid-template-columns: 1fr;
  }

  .form-grid--triple {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 575px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-grid--triple,
  .form-grid--quad {
    grid-template-columns: 1fr;
  }

  .user-form__footer {
    flex-direction: column;
    align-items: stretch;
  }

  .user-form__actions {
    justify-content: flex-end;
  }
}
</style>
